import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Outlet } from 'react-router-dom';

import { queryClient } from '@/shared/api/query-client';
import { AuthProvider } from '@/shared/auth/auth-provider';
import { ErrorBoundary } from '@/shared/components/ui/error-boundary';
import { OfflineBanner } from '@/shared/components/ui/offline-banner';
import { ToastProvider } from '@/shared/components/ui/toast';

/**
 * RootLayout — montado pela `RouterProvider` na raiz da arvore.
 * Concentra providers globais (TanStack Query + AuthProvider + Toast) e
 * componentes "always-on" (OfflineBanner, ErrorBoundary).
 *
 * Ordem dos providers (importa para correto wire-up):
 *  - QueryClientProvider externo: AuthProvider chama /users/me via `api`,
 *    que pode ser inspecionado por DevTools. Tambem o `queryClient.clear()`
 *    no logout e disparado a partir do auth.store, que roda dentro deste
 *    QueryClientProvider.
 *  - AuthProvider em seguida: restaura sessao no boot. Demais consumers
 *    (rotas protegidas, hooks de auth) ja veem store hidratado.
 *  - ToastProvider: precisa estar acima das rotas para useToast funcionar.
 */
export function RootLayout() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ToastProvider>
          <ErrorBoundary>
            <OfflineBanner />
            <Outlet />
          </ErrorBoundary>
        </ToastProvider>
      </AuthProvider>
      {import.meta.env.DEV ? (
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
      ) : null}
    </QueryClientProvider>
  );
}
